import type {
  VolunteerRegistrationPayload,
  VolunteerRegistrationResult,
} from '@sola/schema/dotcom/supporters/volunteers.ts';
import type {
  CompassionSundayBaseFormData,
  CompassionSundayRegistrationFormData,
  LeadChurchRequest,
  LeadEventRequest,
  SubscriptionRequest,
} from '@sola/schema/lib/skippy.ts';
import {
  httpPostWithStatus,
  isSuccessStatus,
} from '@sola/shared-utils/httpClient.ts';
import { withRetry } from '@sola/shared-utils/retry.ts';

const apiRouteBase = '/api';
const compassionSundayRouteBase = `${apiRouteBase}/compassion-sunday`;

/**
 * Send an email subscription request to the Skippy API
 */
export const submitSubscription = async (req: SubscriptionRequest) => {
  // TODO: do we need any CSRF protection here? How do we accomplish that with Next.js?
  const { statusCode } = await httpPostWithStatus(
    `${apiRouteBase}/subscriptions/`,
    req,
    { signRequest: true },
  );
  return statusCode >= 200 && statusCode < 300;
};

/**
 * * Send a Lead request to the Skippy API
 */
export const submitLeadChurchForm =
  (leadType: string, subscriptionCode: string) =>
  async (req: LeadChurchRequest) => {
    const { statusCode } = await httpPostWithStatus(
      `${apiRouteBase}/leads/`,
      {
        ...req,
        leadType,
        subscriptionCode,
      },
      { signRequest: true },
    );
    return statusCode >= 200 && statusCode < 300;
  };

/**
 * * Send a Event Lead request to the Skippy API
 */
export const submitLeadEventForm =
  (leadType: string) => async (req: LeadEventRequest) => {
    const { statusCode } = await httpPostWithStatus(
      `${apiRouteBase}/leads/event`,
      {
        ...req,
        leadType,
      },
      { signRequest: true },
    );
    return statusCode >= 200 && statusCode < 300;
  };

/**
 * Send a Compassion Sunday registration request to the Skippy API
 */
export const submitCompassionSundayRegistration = async (
  req: CompassionSundayRegistrationFormData,
) => {
  const { statusCode } = await httpPostWithStatus(
    `${compassionSundayRouteBase}/registration/`,
    req,
    { signRequest: true },
  );
  return statusCode >= 200 && statusCode < 300;
};

/**
 * Send a Compassion Sunday Plannding Guide request to the Skippy API
 */
export const submitCompassionSundayPlanningGuide = async (
  req: CompassionSundayBaseFormData,
) => {
  const { statusCode } = await httpPostWithStatus(
    `${compassionSundayRouteBase}/planning-guide/`,
    req,
    { signRequest: true },
  );
  return statusCode >= 200 && statusCode < 300;
};

/**
 * Register for volunteer slots at an event location.
 *
 * Wrapped in {@link withRetry} for transient (5xx/network) failures; safe to
 * retry since the server keys on slot IDs. A 401 is surfaced as
 * `sessionExpired` (not retried — auth isn't refreshed here); other non-2xx
 * results carry conflict/validation `errors`.
 */
export const submitVolunteerRegistration = async (
  payload: VolunteerRegistrationPayload,
): Promise<VolunteerRegistrationResult> => {
  const { response, statusCode } = await withRetry(() =>
    httpPostWithStatus<{
      results?: VolunteerRegistrationResult['results'];
      errors?: VolunteerRegistrationResult['errors'];
    }>(`${apiRouteBase}/volunteer/registrations`, payload, {
      signRequest: true,
    }),
  );

  if (isSuccessStatus(statusCode)) {
    return { success: true, results: response?.results };
  }

  // Session expired — caller must re-authenticate.
  if (statusCode === 401) {
    return { success: false, sessionExpired: true };
  }

  // Non-2xx, non-401: conflict/validation failure.
  return {
    success: false,
    errors: response?.errors,
  };
};
